import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DocumentsPageService, UuEvidenceService } from "@/shared/api/services";
import type {
  SharedDocResponse,
  UcEvidencePeriod,
  UpdateDocumentRequest,
  UploadDocumentResponse,
} from "@/shared/api/types";
import { resolveCustomerId } from "@/shared/lib/customerIdResolver";
import type { UploadedDocumentInfo } from "../uuUi/smartSuggestionUU";
import { getUuFieldConfig, isUuFieldName } from "../config/uuFieldConfig";
import { useAlert } from "@/app/providers/AlertProvider";

export interface DocumentPreviewState {
  /** Whether preview dialog is open */
  readonly isOpen: boolean;
  /** Document name for dialog title */
  readonly documentName: string;
  /** Blob URL for preview */
  readonly documentUrl: string | null;
  /** Loading state while fetching document */
  readonly isLoading: boolean;
  /** Error message if document failed to load */
  readonly error: string | null;
  /** MIME type of the document */
  readonly mimeType: string;
}

export interface UuFieldState {
  readonly initialMonth: string;
  readonly initialYear: string;
  readonly isSavingDate: boolean;
  readonly dateError?: string;
  readonly handleDateChange?: (month: string, year: string) => Promise<void>;
  readonly uploadedDocument: UploadedDocumentInfo | null;
  readonly isUploadingDocument: boolean;
  readonly handleUploadDocument?: (
    file: File,
    description?: string
  ) => Promise<void>;
  readonly handleDeleteDocument?: () => Promise<void>;
  /** Opens document in preview dialog instead of new tab */
  readonly handleViewDocument?: () => Promise<void>;
  /** Updates document description (debounced auto-save) */
  readonly handleDescriptionChange?: (description: string) => void;
}

export interface UseUuFieldControllerOptions {
  readonly userId?: number | null;
}

export interface UseUuFieldControllerResult {
  readonly isReady: boolean;
  readonly getFieldState: (fieldName: string) => UuFieldState | undefined;
  /** Preview dialog state for document viewing */
  readonly previewDialog: DocumentPreviewState;
  /** Close the preview dialog */
  readonly closePreviewDialog: () => void;

  readonly refresh: () => Promise<Record<string, UuFieldState>>;
  readonly customerId: string;
}

interface InternalDocumentRecord extends UploadedDocumentInfo {
  readonly id: number;
  readonly documentId: number;
  readonly customerId: number;
  readonly shareId?: string;
  readonly documentName?: string;
  readonly fileName?: string;
  /** The entrySource from API - matches ieFormFieldName */
  readonly entrySource?: string;
}

type DocumentApiPayload = UploadDocumentResponse | SharedDocResponse;

const MONTH_NAMES: readonly string[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isUploadDocumentResponse = (
  value: unknown
): value is UploadDocumentResponse =>
  isRecord(value) &&
  typeof value.id === "number" &&
  typeof value.documentId === "number" &&
  typeof value.customerId === "number" &&
  typeof value.fileName === "string" &&
  typeof value.documentSize === "string";

const isSharedDocResponse = (value: unknown): value is SharedDocResponse =>
  isRecord(value) &&
  typeof value.id === "number" &&
  typeof value.documentId === "number" &&
  typeof value.customerId === "number" &&
  typeof value.fileName === "string" &&
  typeof value.documentSize === "string";

const isApiResponsePayload = (value: unknown): value is { data?: unknown } =>
  isRecord(value) &&
  ("data" in value || "success" in value || "timestamp" in value);

const toMonthName = (value: string | undefined): string => {
  if (!value) {
    return "";
  }
  const parsed = Number(value);
  if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 12) {
    const monthName = MONTH_NAMES[parsed - 1];
    return monthName ?? value;
  }
  return value;
};

const monthNameToNumeric = (month: string): string => {
  if (!month) {
    return "";
  }
  const index = MONTH_NAMES.findIndex(
    (name) => name.toLowerCase() === month.toLowerCase()
  );
  if (index === -1) {
    return "";
  }
  return String(index + 1).padStart(2, "0");
};

const normalizeDocumentSize = (value: string | number | undefined): string => {
  // Prefer the original string from the API so we keep KB/MB units intact
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length > 0) {
      return trimmed;
    }
  }

  // Fall back to a reasonable string when we only have a numeric value
  if (typeof value === "number" && Number.isFinite(value) && value > 0) {
    return `${value} KB`;
  }

  return "";
};

const pickUploadDate = (value?: string): Date => {
  if (value) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      return date;
    }
  }
  return new Date();
};

const toUploadedDocumentInfo = (
  payload: DocumentApiPayload
): InternalDocumentRecord => {
  const base: Omit<InternalDocumentRecord, "shareId" | "entrySource"> = {
    id: payload.id,
    documentId: payload.documentId,
    customerId: payload.customerId,
    name: payload.fileName,
    fileName: payload.fileName,
    documentName: payload.documentName,
    description: payload.documentName,
    size: normalizeDocumentSize(payload.documentSize),
    uploadDate: pickUploadDate(
      payload.uploadDate || payload.creationDate || payload.lastUpdateDate
    ),
  };

  // Build result with optional shareId and entrySource
  const result: InternalDocumentRecord = { ...base };
  if (payload.shareId) {
    (result as Record<string, unknown>).shareId = payload.shareId;
  }
  if (payload.entrySource) {
    (result as Record<string, unknown>).entrySource = payload.entrySource;
  }
  return result;
};

const normalizeDocumentsPayload = (raw: unknown): DocumentApiPayload[] => {
  const pushDocument = (candidate: unknown, acc: DocumentApiPayload[]) => {
    if (isUploadDocumentResponse(candidate) || isSharedDocResponse(candidate)) {
      acc.push(candidate);
    }
  };

  if (Array.isArray(raw)) {
    const bucket: DocumentApiPayload[] = [];
    raw.forEach((item) => pushDocument(item, bucket));
    return bucket;
  }
  if (isApiResponsePayload(raw) && Array.isArray(raw.data)) {
    const bucket: DocumentApiPayload[] = [];
    raw.data.forEach((item) => pushDocument(item, bucket));
    return bucket;
  }
  return [];
};

interface UcEvidenceUpsertRequest {
  readonly customerId: number;
  readonly day: string;
  readonly month: string;
  readonly year: string;
  readonly userId: number;
  readonly entrySource: string;
}

export function useUuFieldController(
  options: UseUuFieldControllerOptions
): UseUuFieldControllerResult {
  const { showError, showSuccess } = useAlert();
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [evidenceByField, setEvidenceByField] = useState<
    Record<string, UcEvidencePeriod>
  >({});
  const [documentsByShare, setDocumentsByShare] = useState<
    Record<string, InternalDocumentRecord>
  >({});
  const [dateSavingState, setDateSavingState] = useState<
    Record<string, boolean>
  >({});
  const [dateErrors, setDateErrors] = useState<
    Record<string, string | undefined>
  >({});
  const [uploadingState, setUploadingState] = useState<Record<string, boolean>>(
    {}
  );
  const [isReady, setIsReady] = useState(false);
  const hasShownLoadError = useRef(false);

  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocumentName, setPreviewDocumentName] = useState("");
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string | null>(
    null
  );
  const [previewIsLoading, setPreviewIsLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewMimeType, setPreviewMimeType] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resolved = await resolveCustomerId();
        if (!cancelled) {
          setCustomerId(resolved);
        }
      } catch (error) {
        if (!cancelled) {
          showError("Unable to determine your customer record.");
          setCustomerId(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showError]);

  useEffect(() => {
    let cancelled = false;
    if (!customerId) {
      setIsReady(true);
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const [evidence, documents] = await Promise.all([
          UuEvidenceService.getCustomerEvidencePeriods(customerId),
          DocumentsPageService.getCustomerDocuments(customerId),
        ]);

        if (cancelled) {
          return;
        }

        const mappedEvidence = evidence.reduce<
          Record<string, UcEvidencePeriod>
        >((acc, record) => {
          if (record.entrySource && isUuFieldName(record.entrySource)) {
            acc[record.entrySource] = record;
          }
          return acc;
        }, {});

        const documentResponses = normalizeDocumentsPayload(documents);
        const mappedDocuments = documentResponses.reduce<
          Record<string, InternalDocumentRecord>
        >((acc, record) => {
          const docInfo = toUploadedDocumentInfo(record);

          // Priority 1: Map by shareId if it starts with "uu-"
          const shareId = record.shareId;
          if (shareId && shareId.startsWith("uu-")) {
            acc[shareId] = docInfo;
          }

          // Priority 2: Map by entrySource (from API response)
          // This allows matching documents to fields via entrySource field
          const entrySource = record.entrySource;
          if (entrySource && isUuFieldName(entrySource)) {
            // Store with uu- prefix to match existing lookup pattern
            const entrySourceKey = `uu-${entrySource}`;
            // Only set if not already set by shareId (shareId takes precedence)
            if (!acc[entrySourceKey]) {
              acc[entrySourceKey] = docInfo;
            }
          }

          return acc;
        }, {});

        setEvidenceByField(mappedEvidence);
        setDocumentsByShare(mappedDocuments);
      } catch (error) {
        if (!cancelled && !hasShownLoadError.current) {
          console.error(error);
          // showError("Unable to load United Utilities guidance right now.", {
          //   autoHide: true,
          //   duration: 6000,
          // });
          hasShownLoadError.current = true;
        }
      } finally {
        if (!cancelled) {
          setIsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customerId, showError]);

  const refresh = useCallback(async (): Promise<
    Record<string, UuFieldState>
  > => {
    if (!customerId) return {};

    try {
      // Fetch evidence and documents from API
      const [evidence, documents] = await Promise.all([
        UuEvidenceService.getCustomerEvidencePeriods(customerId),
        DocumentsPageService.getCustomerDocuments(customerId),
      ]);

      // Map evidence by fieldName
      const mappedEvidence = evidence.reduce<Record<string, UcEvidencePeriod>>(
        (acc, record) => {
          if (record.entrySource && isUuFieldName(record.entrySource)) {
            acc[record.entrySource] = record;
          }
          return acc;
        },
        {}
      );

      // Normalize documents and map by shareId / entrySource
      const documentResponses = normalizeDocumentsPayload(documents);
      const mappedDocuments = documentResponses.reduce<
        Record<string, InternalDocumentRecord>
      >((acc, record) => {
        const docInfo = toUploadedDocumentInfo(record);

        const shareId = record.shareId;
        if (shareId && shareId.startsWith("uu-")) {
          acc[shareId] = docInfo;
        }

        const entrySource = record.entrySource;
        if (entrySource && isUuFieldName(entrySource)) {
          const entrySourceKey = `uu-${entrySource}`;
          if (!acc[entrySourceKey]) acc[entrySourceKey] = docInfo;
        }

        return acc;
      }, {});

      // Update the hook's internal state
      setEvidenceByField(mappedEvidence);
      setDocumentsByShare(mappedDocuments);

      // Build fresh field states immediately
      const fieldStates: Record<string, UuFieldState> = {};

      const fieldNames = Array.from(
        new Set([
          ...Object.keys(mappedEvidence),
          ...Object.keys(mappedDocuments).map((key) => key.replace(/^uu-/, "")),
        ])
      );

      for (const fieldName of fieldNames) {
        if (!isUuFieldName(fieldName)) continue;

        const evidence = mappedEvidence[fieldName];
        const config = getUuFieldConfig(fieldName);
        const uploadedDocument = config?.shareId
          ? (mappedDocuments[config.shareId] ?? null)
          : null;

        const baseState: Omit<
          UuFieldState,
          "dateError" | "handleDeleteDocument" | "handleViewDocument"
        > = {
          initialMonth: evidence ? toMonthName(evidence.month) : "",
          initialYear: evidence?.year ?? "",
          isSavingDate: false,
          handleDateChange: async (month: string, year: string) => {
            if (callbacks.saveDate)
              await callbacks.saveDate(fieldName, month, year);
          },
          uploadedDocument,
          isUploadingDocument: false,
          handleUploadDocument: async (file: File, description?: string) => {
            if (callbacks.upload)
              await callbacks.upload(fieldName, file, description);
          },
        };

        fieldStates[fieldName] = {
          ...baseState,
          ...(uploadedDocument && {
            handleDeleteDocument: () => callbacks.remove?.(fieldName),
            handleViewDocument: () => callbacks.view?.(fieldName),
            handleDescriptionChange: (desc: string) =>
              callbacks.updateDescription?.(fieldName, desc),
          }),
        };
      }

      return fieldStates;
    } catch (error) {
      console.error("Unable to refresh UU data:", error);
      showError("Unable to refresh United Utilities data right now.");
      return {};
    }
  }, [customerId, showError]);

  const saveDateForField = useCallback(
    async (fieldName: string, month: string, year: string) => {
      if (!customerId) {
        showError("Customer details missing. Please reload and try again.");
        return;
      }
      if (!options.userId) {
        showError("Missing user context. Please sign in again.");
        return;
      }
      
      // Handle clearing: if both month and year are empty, DELETE the record
      if (!month && !year) {
        const existing = evidenceByField[fieldName];
        
        // If no existing record, nothing to delete
        if (!existing) {
          return;
        }
        
        setDateSavingState((prev) => ({ ...prev, [fieldName]: true }));
        setDateErrors((prev) => ({ ...prev, [fieldName]: undefined }));
        
        try {
          // DELETE from backend
          await UuEvidenceService.deleteEvidencePeriod(existing.id, customerId);
          
          // Clear from local state after successful API call
          setEvidenceByField((prev) => {
            const next = { ...prev };
            delete next[fieldName];
            return next;
          });
          showSuccess("Benefit start date cleared.");
        } catch (error) {
          setDateErrors((prev) => ({
            ...prev,
            [fieldName]: "We couldn't clear that date. Please try again shortly.",
          }));
          throw error;
        } finally {
          setDateSavingState((prev) => ({ ...prev, [fieldName]: false }));
        }
        return;
      }
      
      const numericMonth = monthNameToNumeric(month);
      if (!numericMonth) {
        setDateErrors((prev) => ({
          ...prev,
          [fieldName]: "Select a valid month.",
        }));
        return;
      }
      if (!year || year.trim().length === 0) {
        setDateErrors((prev) => ({
          ...prev,
          [fieldName]: "Select a valid year.",
        }));
        return;
      }

      setDateSavingState((prev) => ({ ...prev, [fieldName]: true }));
      setDateErrors((prev) => ({ ...prev, [fieldName]: undefined }));

      const payload: UcEvidenceUpsertRequest = {
        customerId,
        day: "00",
        month: numericMonth,
        year,
        userId: options.userId,
        entrySource: fieldName,
      };

      try {
        const existing = evidenceByField[fieldName];
        const saved = existing
          ? await UuEvidenceService.updateEvidencePeriod({
              ...payload,
              id: existing.id,
            })
          : await UuEvidenceService.createEvidencePeriod(payload);

        setEvidenceByField((prev) => ({ ...prev, [fieldName]: saved }));
        showSuccess("Benefit start date saved.");
      } catch (error) {
        setDateErrors((prev) => ({
          ...prev,
          [fieldName]: "We couldn't save that date. Please try again shortly.",
        }));
        throw error;
      } finally {
        setDateSavingState((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [customerId, options.userId, evidenceByField, showError, showSuccess]
  );

  const uploadDocumentForField = useCallback(
    async (fieldName: string, file: File, description?: string) => {
      if (!customerId) {
        showError("Customer details missing. Please reload and try again.");
        return;
      }
      const fieldConfig = getUuFieldConfig(fieldName);
      if (!fieldConfig) {
        return;
      }

      setUploadingState((prev) => ({ ...prev, [fieldName]: true }));
      try {
        const formData = new FormData();
        formData.append("file", file);

        const uploadResponse =
          await DocumentsPageService.uploadCustomerDocument(
            customerId,
            formData,
            "United Utilities",
            fieldName
          );

        const documentTitle =
          description && description.trim().length > 0
            ? description.trim()
            : fieldConfig.documentTitle;

        const updatePayload: UpdateDocumentRequest = {
          id: uploadResponse.id,
          customerId,
          documentId: uploadResponse.documentId,
          documentName: documentTitle,
          documentSize: uploadResponse.documentSize,
          fileName: uploadResponse.fileName,
          shareId: fieldConfig.shareId,
        };
        if (options.userId !== null && options.userId !== undefined) {
          updatePayload.userId = options.userId;
        }

        const updatedDoc =
          await DocumentsPageService.updateCustomerDocument(updatePayload);

        setDocumentsByShare((prev) => ({
          ...prev,
          [fieldConfig.shareId]: toUploadedDocumentInfo(updatedDoc),
        }));
        showSuccess("Document uploaded.");
      } catch (error) {
        showError("We couldn't upload that document. Please try again.");
        throw error;
      } finally {
        setUploadingState((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [customerId, options.userId, showError, showSuccess]
  );

  const deleteDocumentForField = useCallback(
    async (fieldName: string) => {
      if (!customerId) {
        showError("Customer details missing. Please reload and try again.");
        return;
      }
      const config = getUuFieldConfig(fieldName);
      if (!config) {
        return;
      }
      const existing = config.shareId
        ? documentsByShare[config.shareId]
        : undefined;
      if (!existing) {
        return;
      }

      setUploadingState((prev) => ({ ...prev, [fieldName]: true }));
      try {
        await DocumentsPageService.deleteCustomerDocument(
          customerId,
          existing.id
        );
        setDocumentsByShare((prev) => {
          const next = { ...prev };
          delete next[config.shareId];
          return next;
        });
        showSuccess("Document removed.");
      } catch (error) {
        showError("Unable to delete that document at the moment.");
        throw error;
      } finally {
        setUploadingState((prev) => ({ ...prev, [fieldName]: false }));
      }
    },
    [customerId, documentsByShare, showError, showSuccess]
  );

  const closePreviewDialog = useCallback(() => {
    // Revoke blob URL when closing to free memory
    if (previewDocumentUrl) {
      URL.revokeObjectURL(previewDocumentUrl);
    }
    setPreviewDialogOpen(false);
    setPreviewDocumentName("");
    setPreviewDocumentUrl(null);
    setPreviewIsLoading(false);
    setPreviewError(null);
    setPreviewMimeType("");
  }, [previewDocumentUrl]);

  const viewDocumentForField = useCallback(
    async (fieldName: string) => {
      const config = getUuFieldConfig(fieldName);
      if (!config) {
        return;
      }
      const existing = config.shareId
        ? documentsByShare[config.shareId]
        : undefined;
      if (!existing) {
        showError("No document available to preview.");
        return;
      }

      // Open dialog immediately with loading state
      setPreviewDialogOpen(true);
      setPreviewDocumentName(
        existing.documentName || existing.name || "Document Preview"
      );
      setPreviewDocumentUrl(null);
      setPreviewIsLoading(true);
      setPreviewError(null);
      setPreviewMimeType("");

      try {
        const blob = await DocumentsPageService.downloadCustomerDocument(
          existing.documentId
        );
        if (typeof window === "undefined" || !(blob instanceof Blob)) {
          setPreviewError("Preview is not supported in this environment.");
          setPreviewIsLoading(false);
          return;
        }
        const url = URL.createObjectURL(blob);
        setPreviewDocumentUrl(url);
        setPreviewMimeType(blob.type || "");
        setPreviewIsLoading(false);
      } catch (error) {
        setPreviewError("Unable to download that document right now.");
        setPreviewIsLoading(false);
      }
    },
    [documentsByShare, showError]
  );

  const updateDescriptionForField = useCallback(
    async (fieldName: string, description: string) => {
      if (!customerId) {
        return;
      }
      const config = getUuFieldConfig(fieldName);
      if (!config) {
        return;
      }
      const existing = config.shareId
        ? documentsByShare[config.shareId]
        : undefined;
      if (!existing) {
        return;
      }

      const trimmedDescription = description.trim();

      // Skip if description (ignoring whitespace) hasn't changed
      if ((existing.description ?? "") === trimmedDescription) {
        return;
      }

      try {
        const updatePayload: UpdateDocumentRequest = {
          id: existing.id,
          customerId,
          documentId: existing.documentId,
          // Allow blank documentName so clearing notes persists as empty
          documentName: trimmedDescription,
          documentSize: String(existing.size),
          fileName: existing.fileName || existing.name,
          shareId: config.shareId,
        };
        if (options.userId !== null && options.userId !== undefined) {
          updatePayload.userId = options.userId;
        }

        const updatedDoc =
          await DocumentsPageService.updateCustomerDocument(updatePayload);

        const normalizedDoc = toUploadedDocumentInfo(updatedDoc);
        const finalDescription =
          normalizedDoc.description ?? trimmedDescription;
        // Ensure local cache reflects what we tried to save, even if API omits documentName
        normalizedDoc.description = finalDescription;
        normalizedDoc.documentName =
          normalizedDoc.documentName ?? finalDescription;

        setDocumentsByShare((prev) => ({
          ...prev,
          [config.shareId]: normalizedDoc,
        }));
      } catch (error) {
        // Silently fail for description updates to avoid disrupting UX
        console.error("Failed to update document description:", error);
      }
    },
    [customerId, documentsByShare, options.userId]
  );

  const getFieldState = useCallback(
    (fieldName: string): UuFieldState | undefined => {
      if (!isUuFieldName(fieldName)) {
        return undefined;
      }
      const evidence = evidenceByField[fieldName];
      const config = getUuFieldConfig(fieldName);
      const uploadedDocument = config?.shareId
        ? (documentsByShare[config.shareId] ?? null)
        : null;

      const dateError = dateErrors[fieldName];
      const baseState: Omit<
        UuFieldState,
        "dateError" | "handleDeleteDocument" | "handleViewDocument"
      > = {
        initialMonth: evidence ? toMonthName(evidence.month) : "",
        initialYear: evidence?.year ?? "",
        isSavingDate: Boolean(dateSavingState[fieldName]),
        handleDateChange: (month: string, year: string) =>
          saveDateForField(fieldName, month, year),
        uploadedDocument,
        isUploadingDocument: Boolean(uploadingState[fieldName]),
        handleUploadDocument: (file: File, docDescription?: string) =>
          uploadDocumentForField(fieldName, file, docDescription),
      };

      return {
        ...baseState,
        ...(dateError !== undefined && { dateError }),
        ...(uploadedDocument && {
          handleDeleteDocument: () => deleteDocumentForField(fieldName),
          handleViewDocument: () => viewDocumentForField(fieldName),
          handleDescriptionChange: (desc: string) =>
            void updateDescriptionForField(fieldName, desc),
        }),
      };
    },
    [
      dateErrors,
      dateSavingState,
      deleteDocumentForField,
      documentsByShare,
      evidenceByField,
      saveDateForField,
      updateDescriptionForField,
      uploadDocumentForField,
      uploadingState,
      viewDocumentForField,
    ]
  );

  const previewDialog: DocumentPreviewState = useMemo(
    () => ({
      isOpen: previewDialogOpen,
      documentName: previewDocumentName,
      documentUrl: previewDocumentUrl,
      isLoading: previewIsLoading,
      error: previewError,
      mimeType: previewMimeType,
    }),
    [
      previewDialogOpen,
      previewDocumentName,
      previewDocumentUrl,
      previewIsLoading,
      previewError,
      previewMimeType,
    ]
  );

  return useMemo(
    () => ({
      isReady,
      getFieldState,
      previewDialog,
      closePreviewDialog,
      refresh,
      customerId,
    }),
    [getFieldState, isReady, previewDialog, closePreviewDialog, refresh]
  );
}
